import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";


import "bootstrap/dist/css/bootstrap.min.css";
import "./venue.css";
import { Country, State, City } from "country-state-city";

const VenuePage = () => {
  const [venues, setVenues] = useState([]);
  const [filteredVenues, setFilteredVenues] = useState([]);
  const [filter, setFilter] = useState("");

  const [selectedVenue, setSelectedVenue] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState(""); // "full" or "partial"
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [loadingProvider, setLoadingProvider] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;
  const storedUser = sessionStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : null;
  const [filters, setFilters] = useState({
    category: null,
    subcategory: null,
    capacity: null,
    price: null,
    rating: null,
    shortlisted: false,
    country: null,
    state: null,
    city: null,
  });

  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    subcategories: [],
    capacities: [],
    prices: [],
    ratings: [],
    countries: [],
    states: [],
    cities: [],
  });
const navigate = useNavigate();
const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const CONVENIENCE_FEE_PERCENT = 0.01;
  const PARTIAL_PAYMENT_PERCENT = 0.01;

  const inputRef = useRef(null);

  const calculateServiceCost = (baseAmount, type) => {
    if (type === "partial") {
      return baseAmount * PARTIAL_PAYMENT_PERCENT;
    }
    return baseAmount;
  };

  const calculateConvenienceFee = (serviceCost) =>
    serviceCost * CONVENIENCE_FEE_PERCENT;

  const calculateTotalAmount = (baseAmount, type) => {
    const serviceCost = calculateServiceCost(baseAmount, type);
    return serviceCost + calculateConvenienceFee(serviceCost);
  };

  useEffect(() => {
    fetchVenues();

    // load countries and prioritize India
    let countries = Country.getAllCountries();
    const india = countries.find((c) => c.isoCode === "IN");
    if (india) {
      countries = [india, ...countries.filter((c) => c.isoCode !== "IN")];
    }
    setFilterOptions((prev) => ({
      ...prev,
      countries: [india],
    }));
  }, []);

  const fetchVenues = async () => {
    try {
      const res = await axios.get(`${API_URL}/catagory`);
      const venueData = res.data.filter(
        (item) => item.category && item.category.toLowerCase() === "venues"
      );
      setVenues(venueData);
      setFilteredVenues(venueData);

      // Extract unique options for filters
      const categories = [...new Set(venueData.map((v) => v.category))];
      const subcategories = [
        ...new Set(
          venueData.flatMap((v) =>
            Array.isArray(v.subcategory) ? v.subcategory : [v.subcategory]
          )
        ),
      ];
      const capacities = [
        ...new Set(venueData.map((v) => v.capacity).filter(Boolean)),
      ];
      const prices = [
        ...new Set(venueData.map((v) => v.price).filter(Boolean)),
      ];
      const ratings = [
        ...new Set(venueData.map((v) => v.rating).filter(Boolean)),
      ];

      setFilterOptions((prev) => ({
        ...prev,
        categories,
        subcategories,
        capacities,
        prices,
        ratings,
      }));
    } catch (err) {
      console.error("Error fetching venues:", err);
    }
  };

  // update states when country changes
  useEffect(() => {
    if (filters.country === "IN") {
      // only Kerala, Tamil Nadu, Karnataka
      const allowedStates = ["KL", "TN", "KA"]; // isoCodes
      let states = State.getStatesOfCountry("IN").filter((s) =>
        allowedStates.includes(s.isoCode)
      );

      setFilterOptions((prev) => ({ ...prev, states, cities: [] }));
      setFilters((prev) => ({ ...prev, state: null, city: null }));
    }
  }, [filters.country]);

  // update cities when state changes
  useEffect(() => {
    if (filters.country === "IN" && filters.state) {
      let cities = City.getCitiesOfState(filters.country, filters.state);
      setFilterOptions((prev) => ({ ...prev, cities }));
      setFilters((prev) => ({ ...prev, city: null }));
    }
  }, [filters.state, filters.country]);

  // apply filters + search
  useEffect(() => {
    let data = [...venues];

    if (filters.category) {
      data = data.filter((v) => v.category === filters.category);
    }
    if (filters.subcategory) {
      data = data.filter((v) =>
        Array.isArray(v.subcategory)
          ? v.subcategory.includes(filters.subcategory)
          : v.subcategory === filters.subcategory
      );
    }
    if (filters.capacity) {
      data = data.filter((v) => v.capacity === filters.capacity);
    }
    if (filters.price) {
      data = data.filter((v) => v.price === filters.price);
    }
    if (filters.rating) {
      data = data.filter((v) => v.rating === filters.rating);
    }
    if (filters.shortlisted) {
      data = data.filter((v) => v.shortlisted === true);
    }

    // filter by country/state/city (match against venue.location)
    if (filters.city) {
      data = data.filter((v) =>
        v.location?.toLowerCase().includes(filters.city.toLowerCase())
      );
    } else if (filters.state) {
      const stateName = filterOptions.states.find(
        (s) => s.isoCode === filters.state
      )?.name;
      if (stateName) {
        data = data.filter((v) =>
          v.location?.toLowerCase().includes(stateName.toLowerCase())
        );
      }
    } else if (filters.country) {
      const countryName = filterOptions.countries.find(
        (c) => c.isoCode === filters.country
      )?.name;
      if (countryName) {
        data = data.filter((v) =>
          v.location?.toLowerCase().includes(countryName.toLowerCase())
        );
      }
    }

    if (filter.trim()) {
      const search = filter.toLowerCase();
      data = data.filter((venue) => {
        const nameMatch = venue.name?.toLowerCase().includes(search);
        const locationMatch = venue.location?.toLowerCase().includes(search);
        const subcatMatch = Array.isArray(venue.subcategory)
          ? venue.subcategory.some((sub) => sub?.toLowerCase().includes(search))
          : venue.subcategory?.toLowerCase().includes(search);
        return nameMatch || locationMatch || subcatMatch;
      });
    }

    setFilteredVenues(data);
  }, [filters, venues, filter, filterOptions]);

const handleOpenModal = async (venue) => {
  setSelectedVenue(venue);
  setShowModal(true);
  setLoadingProvider(true);

  if (!venue?.venueId) {
    console.warn("Venue ID is missing:", venue);
    setLoadingProvider(false);
    return;
  }

};
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedVenue(null);
  };

  const handleOpenPaymentModal = (type) => {
      // ✅ Check if user is logged in
  if (!parsedUser) {
    setShowModal(false); // close venue details modal
    setShowLoginPrompt(true); // open login/register prompt
    return;
  }
    if (!selectedVenue) return;
    
    setPaymentType(type);
    
    const base = Number(selectedVenue.price) || 0;
    const amount = calculateTotalAmount(base, type);
    
    setPaymentAmount(amount);
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentType("");
    setPaymentAmount(0);
  };

 

const handlePayment = async () => {
  try {
    const storedUser = sessionStorage.getItem("user");
    const user = storedUser ? JSON.parse(storedUser) : null;
    

    const orderData = {
      orderId: `order_${Date.now()}`,
            userId:user?.id,
            amountPaid: paymentAmount,
      order_amount: paymentAmount, // amount based on full/partial
      customer_details: {
        customer_id: user?.id || "guest_user",
        customer_name: user?.name || "Guest",
        customer_email: user?.email || "guest@example.com",
        customer_phone: user?.phone || "9999999999",
      },
      order_meta: {
        return_url: `${window.location.origin}/payment-success?order_id={order_id}`,
      },
      venueId: selectedVenue._id,
      venueName: selectedVenue.name,
            uploadedBy:selectedVenue.uploadedBy,

      paymentType,
    };
    // 1️⃣ Create Cashfree Order via backend
    const res = await axios.post(`${API_URL}/orders`,orderData);

    const { payment_session_id } = res.data;

    if (!payment_session_id) {
      alert("❌ Payment session missing. Check backend response.");
      return;
    }

    // 2️⃣ Redirect to Cashfree Checkout
    const cf = new window.Cashfree(payment_session_id);
    cf.redirect();
        await axios.post(`${API_URL}/orders/createorder`, orderData);

  } catch (err) {
    console.error("Payment failed:", err.response?.data || err.message);
    alert("❌ Payment failed. Please try again.");
  }
};

const handleCloseLoginPrompt = () => {
    setShowLoginPrompt(false);
  };


  return (
    <div className="venue-page">
      <h1 className="custom-heading">Our Venues</h1>

      {/* 🔹 Filters */}
      <div className="vendor-filters d-flex gap-3 mb-4 flex-wrap">
        <select
          className="vendor-select"
          onChange={(e) =>
            setFilters({ ...filters, country: e.target.value || null })
          }
        >
          <option value="">Select Country</option>
          {filterOptions.countries.map((c) => (
            <option key={c.isoCode} value={c.isoCode}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          className="vendor-select"
          onChange={(e) =>
            setFilters({ ...filters, state: e.target.value || null })
          }
          disabled={!filters.country}
        >
          <option value="">Select State</option>
          {filterOptions.states.map((s) => (
            <option key={s.isoCode} value={s.isoCode}>
              {s.name}
            </option>
          ))}
        </select>

        <select
          className="vendor-select"
          onChange={(e) =>
            setFilters({ ...filters, city: e.target.value || null })
          }
          disabled={!filters.state}
        >
          <option value="">Select City/District</option>
          {filterOptions.cities.map((city, i) => (
            <option key={i} value={city.name}>
              {city.name}
            </option>
          ))}
        </select>
        <select
          className="vendor-select"
          onChange={(e) =>
            setFilters({ ...filters, category: e.target.value || null })
          }
        >
          <option value="">Select Category</option>
          {filterOptions.categories.map((cat, i) => (
            <option key={i} value={cat}>
              {cat}
            </option>
          ))}
        </select>

        <select
          className="vendor-select"
          onChange={(e) =>
            setFilters({ ...filters, subcategory: e.target.value || null })
          }
        >
          <option value="">Select Subcategory</option>
          {filterOptions.subcategories.map((sub, i) => (
            <option key={i} value={sub}>
              {sub}
            </option>
          ))}
        </select>

        <select
          className="vendor-select"
          onChange={(e) =>
            setFilters({ ...filters, capacity: e.target.value || null })
          }
        >
          <option value="">Select Capacity</option>
          {filterOptions.capacities.map((cap, i) => (
            <option key={i} value={cap}>
              {cap}
            </option>
          ))}
        </select>

        <select
          className="vendor-select"
          onChange={(e) =>
            setFilters({ ...filters, price: e.target.value || null })
          }
        >
          <option value="">Select Price</option>
          {filterOptions.prices.map((price, i) => (
            <option key={i} value={price}>
              {price}
            </option>
          ))}
        </select>

        <select
          className="vendor-select"
          onChange={(e) =>
            setFilters({ ...filters, rating: e.target.value || null })
          }
        >
          <option value="">Select Rating</option>
          {filterOptions.ratings.map((rating, i) => (
            <option key={i} value={rating}>
              {rating}
            </option>
          ))}
        </select>
      </div>

      {/* 🔹 Search */}
      <div className="venue-search mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search venues by name, location or type"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          ref={inputRef}
        />
      </div>

      {/* 🔹 Venue Cards */}
      <div className="venue-cards">
        {filteredVenues.length > 0 ? (
          filteredVenues.map((venue) => (
            <div className="venue-card" key={venue._id}>
              {Array.isArray(venue.images) && venue.images.length > 0 ? (
                <img
                  src={`${API_URL}/${venue.images[0]}`}
                  alt={venue.name}
                  className="venue-image"
                />
              ) : (
                venue.image && (
                  <img
                    src={`${API_URL}/${venue.image}`}
                    alt={venue.name}
                    className="venue-image"
                  />
                )
              )}
              <h5>{venue.name}</h5>
              <p>
                <strong>Location:</strong> {venue.location}
              </p>
              <p>
                <strong>Capacity:</strong> {venue.capacity}
              </p>
              <p>
                <strong>Category:</strong> {venue.category}
              </p>
              <p>
                <strong>Price:</strong> {venue.price}
              </p>
              {venue.subcategory && (
                <p>
                  <strong>Subcategory:</strong>{" "}
                  {Array.isArray(venue.subcategory)
                    ? venue.subcategory.join(", ")
                    : venue.subcategory}
                </p>
              )}
              {venue.services && venue.services.length > 0 && (
                <div className="venue-services">
                  <h4>Services Offered</h4>
                  <ul>
                    {venue.services.map((service, i) => (
                      <li key={i}>✔ {service}</li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                className="btn btn-primary"
                onClick={() => handleOpenModal(venue)}
              >
                View Details
              </button>
            </div>
          ))
        ) : (
          <p>No venues found.</p>
        )}
      </div>

      {/* 🔹 Custom Canva-style Modal */}
      {showModal && (
        <div className="custom-modal-overlay" onClick={handleCloseModal}>
          <div className="custom-modal" onClick={(e) => e.stopPropagation()}>
            <div className="custom-modal-header d-flex justify-content-between align-items-center">
              <h3 className="modal-title">{selectedVenue?.name || "Venue Details"}</h3>
              <button className="close-btn" onClick={handleCloseModal}>✕</button>
            </div>

            <div className="custom-modal-body">
              {selectedVenue ? (
                <>
                  <div className="venue-images-scroll mb-4">
                    {selectedVenue.images && selectedVenue.images.length > 0 ? (
                      selectedVenue.images.map((img, i) => (
                        <div key={i} className="venue-img-wrapper">
                          <img
                            src={`${API_URL}/${img}`}
                            alt={`venue-${i}`}
                            className="venue-img-scroll"
                          />
                        </div>
                      ))
                    ) : selectedVenue.image ? (
                      <img
                        src={`${API_URL}/${selectedVenue.image}`}
                        alt={selectedVenue.name}
                        className="venue-img-scroll"
                      />
                    ) : (
                      <div className="no-image-placeholder">No Images Available</div>
                    )}

                    {selectedVenue.video && (
                      <video
                        controls
                        className="venue-video mt-2"
                        width="100%"
                        style={{ borderRadius: "12px" }}
                      >
                        <source src={`${API_URL}/${selectedVenue.video}`} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>

                  <div className="venue-info-section row">
                    <div className="col-md-6">
                      <p><strong>Location:</strong> {selectedVenue.location}</p>
                      <p><strong>Price:</strong> ₹{selectedVenue.price || "N/A"}</p>
                      <p><strong>Capacity:</strong> {selectedVenue.capacity || "N/A"} Guests</p>
                      <p><strong>Reviews:</strong> ⭐ {selectedVenue.reviews || "0"} / 5</p>
                      <p><strong>Description:</strong> {selectedVenue.description || "No description available."}</p>
                    </div>

                    <div className="col-md-6">
                      {selectedVenue.services && selectedVenue.services.length > 0 && (
                        <div className="venue-services border p-3 rounded">
                          <strong>Services:</strong>
                          <ul>
                            {selectedVenue.services.map((service, i) => (
                              <li key={i}>✔ {service}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="modal-buttons d-flex flex-row gap-3 mt-4">
                    <button
                      className="btn btn-primary text-start flex-fill payment-btn"
                      onClick={() => handleOpenPaymentModal("full")}
                    >
                      <strong>
                       ₹{calculateTotalAmount(Number(selectedVenue?.price) || 0, "full").toFixed(2)}&nbsp;Pay Full

                      </strong>
                    </button>

                    <button
                      className="btn btn-success text-start flex-fill payment-btn"
                      onClick={() => handleOpenPaymentModal("partial")}
                    >
                      <strong>
                      ₹{calculateTotalAmount(Number(selectedVenue?.price) || 0, "partial").toFixed(2)}&nbsp;Pay 1%

                      </strong>
                    </button>
                  </div>

                  <div className="payment-breakdown mt-3">
                    <p>
                     <strong>*Pay Full:</strong> ₹{calculateServiceCost(Number(selectedVenue?.price) || 0, "full").toFixed(2)} 
+ ₹{calculateConvenienceFee(calculateServiceCost(Number(selectedVenue?.price) || 0, "full")).toFixed(2)} (1% Convenience)  
= <strong>₹{calculateTotalAmount(Number(selectedVenue?.price) || 0, "full").toFixed(2)}</strong>

                    </p>
                    <p>
             <strong>*Pay 1%:</strong> ₹{calculateServiceCost(Number(selectedVenue?.price) || 0, "partial").toFixed(2)} 
+ ₹{calculateConvenienceFee(calculateServiceCost(Number(selectedVenue?.price) || 0, "partial")).toFixed(2)} (1% Convenience)  
= <strong>₹{calculateTotalAmount(Number(selectedVenue?.price) || 0, "partial").toFixed(2)}</strong>

                    </p>
                  </div>
                </>
              ) : (
                <p>Loading...</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🔹 Payment Modal */}
   {showPaymentModal && (
  <div className="custom-modal-overlay" onClick={handleClosePaymentModal}>
    <div
      className="custom-modal payment-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="custom-modal-header">
        <h3 className="modal-title">Payment Details</h3>
        <button className="close-btn" onClick={handleClosePaymentModal}>
          ✕
        </button>
      </div>

      <div className="custom-modal-body">
        <div className="container rounded bg-white">
          <div className="row d-flex justify-content-center pb-5">
            {/* Left section */}
            <div className="col-sm-5 col-md-5 ml-1">
              <div className="py-4 d-flex flex-row">
                <h5>
                  <span className="fa fa-check-square-o"></span>
                  <b> ELIGIBLE</b> |
                </h5>
                <span className="pl-2">Pay</span>
              </div>
              <h4 className="green">₹{paymentAmount.toFixed(2)}</h4>
              <h4>{selectedVenue?.name}</h4>

              <div className="d-flex pt-2">
                <p>
                  <b>Service Cost</b>{" "}
                  <span className="green">
                    ₹{calculateServiceCost(Number(selectedVenue?.price) || 0, paymentType).toFixed(2)}
                  </span>
                </p>
              </div>
              <p>
                Payment will be processed securely through our payment gateway
              </p>

              <div className="rounded bg-light d-flex">
                <div className="p-2">Online Payment</div>
                <div className="ml-auto p-2">Secure</div>
              </div>

              <hr />
{/* 🧍 Customer Details */}
<div className="mt-4 p-3 border rounded bg-light">
  <h5>Customer Details</h5>
  {parsedUser ? (
    <>
      <p><strong>Name:</strong> {parsedUser.name}</p>
      <p><strong>Phone:</strong> {parsedUser.phone}</p>
      <p><strong>Email:</strong> {parsedUser.email}</p>
    </>
  ) : (
    <p>Please login to view customer details.</p>
  )}
</div>

{/* 🏢 Venue Provider Details */}
<div className="mt-4 p-3 border rounded bg-light">
  <h5>Venue Provider Details</h5>

  {loadingProvider ? (
    
    <p>Loading provider information...</p>
  ) : selectedVenue ? (
    
    
    <>
      <p><strong>Name:</strong> {selectedVenue.name || "N/A"}</p>
      <p><strong>Phone:</strong> {selectedVenue.phone || "N/A"}</p>
      <p><strong>Email:</strong> {selectedVenue.email || "N/A"}</p>
      <p><strong>Business Name:</strong> {selectedVenue.businessName || "N/A"}</p>
    </>
  ) : (
    <p>No provider information available.</p>
  )}
</div>

              {/* Payment options */}
              <div className="pt-2">
            


                <div>
                  <input
                    type="button"
                    value="Proceed to payment"
                    className="btn btn-primary btn-block"
                    onClick={handlePayment}
                  />
                </div>
              </div>
            </div>

            {/* Right section (Order Recap) */}
            <div className="col-sm-3 col-md-4 offset-md-1 mobile">
              <div className="py-4 d-flex justify-content-end">
                <h6>
                  <a
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleClosePaymentModal();
                    }}
                  >
                    Cancel and return to website
                  </a>
                </h6>
              </div>

              <div className="bg-light rounded d-flex flex-column">
                <div className="p-2 ml-3">
                  <h4>Order Recap</h4>
                </div>
                <div className="p-2 d-flex">
                  <div className="col-8">Service Cost</div>
                  <div className="ml-auto">
              ₹{calculateServiceCost(Number(selectedVenue?.price) || 0, paymentType).toFixed(2)}

                  </div>
                </div>
                <div className="p-2 d-flex">
                  <div className="col-8">Convenience Fee</div>
                  <div className="ml-auto">
              + ₹{calculateConvenienceFee(calculateServiceCost(Number(selectedVenue?.price) || 0, paymentType)).toFixed(2)}

                  </div>
                </div>
                <div className="border-top px-4 mx-3"></div>
                <div className="p-2 d-flex pt-3">
                  <div className="col-8">
                    <b>Total Amount</b>
                  </div>
                  <div className="ml-auto">
                    <b className="green">₹{paymentAmount.toFixed(2)}</b>
                  </div>
                </div>
              </div>
            </div>
            {/* End Right */}
          </div>
        </div>
      </div>
    </div>
  </div>
)}

{showLoginPrompt && (
  <div className="custom-modal-overlay" onClick={() => setShowLoginPrompt(false)}>
    <div
      className="custom-modal small-modal"
      onClick={(e) => e.stopPropagation()}
      style={{ textAlign: "center", padding: "20px" }}
    >  
      <p>Do you already have an account?</p>

      <div className="d-flex justify-content-around mt-4">
        <button
          className="btn btn-primary"
          onClick={() => {
            setShowLoginPrompt(false);
            navigate("/loginsignup");
          }}
        >
          Yes, I have an account
        </button>

        <button
          className="btn btn-success"
          onClick={() => {
            setShowLoginPrompt(false);
            navigate("/registration");
          }}
        >
          No, I’m new here
        </button>
      </div>
    </div>
  </div>
)}


    </div>
  );
};

export default VenuePage;